import { Spinner } from '@/components/loaders/Spinner';
import { cn } from '@/utils/cn';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-[14px] leading-[20px] font-normal transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-primary focus-visible:ring-primary/50 focus-visible:ring-[3px]",
  {
    variants: {
      variant: {
        default:
          'bg-neutral-950 text-white border border-neutral-950 hover:bg-neutral-950/90 hover:border-neutral-950/90 active:bg-neutral-950 active:border-neutral-950 [&_svg]:text-white',
        secondary:
          'bg-neutral-50 text-neutral-950 border border-neutral-200 hover:bg-neutral-100 hover:border-neutral-300 active:bg-neutral-200 [&_svg]:text-neutral-950',
        destructive:
          'bg-error-500 text-white border border-error-500 hover:bg-error-400 hover:border-error-400 active:bg-error-600 focus-visible:ring-error-500/20 [&_svg]:text-white',
        success:
          'bg-success-700 text-white border border-success-700 hover:bg-success-800 hover:border-success-800 active:bg-success-900 focus-visible:ring-success-500/20 [&_svg]:text-white',
        warning:
          'bg-warning-700 text-white border border-warning-700 hover:bg-warning-800 hover:border-warning-800 active:bg-warning-900 focus-visible:ring-warning-500/20 [&_svg]:text-white',
        approval:
          'bg-blue-800 text-white border border-blue-800 hover:bg-blue-900 hover:border-blue-900 active:bg-blue-900 active:border-blue-900 focus-visible:ring-blue-700/50 [&_svg]:text-white',
        postponement:
          'bg-rose-600 text-white border border-rose-600 hover:bg-rose-700 hover:border-rose-700 active:bg-rose-700 active:border-rose-700 focus-visible:ring-rose-600/50 [&_svg]:text-white',
        primary:
          'bg-primary-500 text-white border border-primary-500 hover:bg-primary-600 hover:border-primary-600 active:bg-primary-800 active:border-primary-800 focus-visible:ring-primary-500/50 [&_svg]:text-white',
        outline:
          'border border-neutral-200 bg-background text-neutral-950 shadow-low hover:bg-neutral-100 hover:text-neutral-950 [&_svg]:text-neutral-950',
        ghost: 'hover:bg-neutral-100 hover:text-neutral-950 [&_svg]:text-current',
        link: 'text-primary underline-offset-4 hover:underline',
        dark: 'bg-secondary-900 text-white border border-secondary-900 hover:bg-secondary-600 hover:border-secondary-600 active:bg-secondary-900 active:border-secondary-900 [&_svg]:text-white',
        text: 'bg-transparent text-neutral-950 border border-transparent hover:bg-neutral-100 active:bg-neutral-200 [&_svg]:text-neutral-950',
        inline: 'bg-transparent border border-transparent focus-visible:ring-0 [&_svg]:text-current',
        tab: 'relative flex h-full items-center gap-2 px-3 text-sm font-normal transition-colors rounded-none border-none text-neutral-500 hover:text-neutral-950 data-[active=true]:text-neutral-950 data-[active=true]:bg-white data-[active=true]:after:bg-primary-500 data-[active=true]:after:absolute data-[active=true]:after:bottom-0 data-[active=true]:after:left-0 data-[active=true]:after:h-0.5 data-[active=true]:after:w-full focus-visible:ring-0'
      },
      size: {
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        xs: 'h-5 px-2 py-0.5 text-xxs',
        condensed: '',
        icon: 'size-9',
        'icon-sm': 'size-8',
        'icon-lg': 'size-10'
      }
    },
    defaultVariants: {
      variant: 'default',
      size: 'default'
    }
  }
);

export type ButtonVariantProps = VariantProps<typeof buttonVariants>;
export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariantProps {
  asChild?: boolean;
  loading?: boolean;
  ref?: React.RefObject<HTMLButtonElement>;
}

const Button = ({ className, variant, size, asChild = false, ref, loading, children, ...props }: ButtonProps) => {
  const Comp = asChild ? Slot : 'button';
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }), {
        'pointer-events-none relative disabled:opacity-100': loading,
        'disabled:opacity-50': loading && props.disabled
      })}
      ref={ref}
      disabled={loading ?? props.disabled}
      {...props}
    >
      {loading ? (
        <>
          <span className="invisible flex items-center gap-2">{children}</span>
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner variant="default" size="sm" />
          </span>
        </>
      ) : (
        children
      )}
    </Comp>
  );
};
Button.displayName = 'Button';

export { Button, buttonVariants };
